import Cookies from 'js-cookie';

/**
 * Checks if the user has granted OneTrust "Targeting Cookies" consent (category C0004).
 * For US visitors (opt-out model), OneTrust grants all categories by default.
 * For UK/EU/CA visitors (opt-in model), this returns false until explicit acceptance.
 */
export function hasTargetingConsent(): boolean {
  try {
    return ((window as any).OptanonActiveGroups || '').indexOf('C0004') !== -1;
  } catch {
    return false;
  }
}

/** EU member state + UK country codes requiring explicit marketing consent under GDPR. */
const GDPR_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
]);

/**
 * Where the visitor is, as detected: OneTrust's geolocation, then the viewer_country cookie the edge
 * sets from CloudFront's GeoIP. Never the market the shopper picked, which the country selector and a
 * language tree's URL (/fr-ca puts anyone in Canada) both set.
 */
function detectedCountry(): string | undefined {
  let geo: string | undefined;
  try {
    geo = window.OneTrust?.getGeolocationData?.()?.country;
  } catch {
    // OneTrust may not be loaded yet
  }
  return knownCountry(geo) ?? knownCountry(Cookies.get('viewer_country'));
}

// OneTrust reports a location it can't place as XX, CloudFront's GeoIP as ZZ
const knownCountry = (code: string | undefined) => {
  const upper = code?.toUpperCase();
  return upper && upper !== 'XX' && upper !== 'ZZ' ? upper : undefined;
};

/**
 * Whether checkout must ask for explicit marketing consent: by the visitor's detected location, and
 * always when that location is unknown, so a GDPR visitor is never opted in by default.
 */
export function requiresCheckoutMarketingConsent(): boolean {
  return requiresMarketingConsent();
}

/**
 * Whether marketing storage (a persistent anonymous ID, GrowthBook's gbexp_* sticky buckets) waits for consent: by
 * where the visitor is, and always when that's unknown. Not the store they shop in, so a stale US store cookie never
 * opts a visitor in Germany in.
 */
export function requiresMarketingConsent(): boolean {
  const country = detectedCountry();
  return country === undefined || GDPR_COUNTRY_CODES.has(country);
}
